import { type TextInputValue, type TextInputVariant } from './types.js';
import { dayjs } from '@contra/date';
import { Money } from '@contra/money';
import { UseFormRegisterReturn } from 'react-hook-form';

const numberWithSeparatorsFormatter = new Intl.NumberFormat('en-US');

const decimalNumberWithSeparatorsFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

export const serializeValue = <TVariant extends TextInputVariant>({
  allowDecimals,
  event,
  variant,
}: {
  allowDecimals: boolean;
  event: React.ChangeEvent<HTMLInputElement>;
  variant: TVariant;
}): TextInputValue<TVariant> | undefined => {
  const value = event.target.value;

  if (value === '') {
    return undefined;
  }

  if (variant === 'usd') {
    let formattedValue = value.replaceAll(/[^\d.]/g, '');

    if (allowDecimals) {
      formattedValue = formattedValue.split('.').join('');
      formattedValue =
        formattedValue.length === 1 ? '0' + formattedValue : formattedValue;
      formattedValue =
        formattedValue.slice(0, -2) + '.' + formattedValue.slice(-2);
    } else {
      formattedValue = formattedValue.split('.')[0];
    }

    return Money.fromUsdDollars(formattedValue) as TextInputValue<TVariant>;
  }

  if (variant === 'number' || variant === 'percent') {
    let formattedValue = value;

    if (!allowDecimals) {
      formattedValue = value.split('.')[0];
    }

    return Number(formattedValue) as TextInputValue<TVariant>;
  }

  if (variant === 'date') {
    // This prevents us from converting a partial date
    if (
      value.startsWith('0') ||
      value.startsWith('00') ||
      value.startsWith('000')
    ) {
      return undefined;
    }

    return dayjs(value).toDate() as TextInputValue<TVariant>;
  }

  return value as TextInputValue<TVariant>;
};

export const deserializeValue = <TVariant extends TextInputVariant>({
  addCurrencyPrefix,
  allowDecimals,
  value,
  variant,
}: {
  addCurrencyPrefix?: boolean;
  allowDecimals: boolean;
  value?: TextInputValue<TVariant>;
  variant: TVariant;
}): string | undefined => {
  if (value instanceof Money) {
    if (allowDecimals) {
      const moneyValue = decimalNumberWithSeparatorsFormatter.format(
        value.toUnsafeNumber(),
      );

      if (addCurrencyPrefix) {
        return `$${moneyValue}`;
      }

      return moneyValue;
    } else {
      return numberWithSeparatorsFormatter.format(value.toUnsafeNumber());
    }
  }

  if (value instanceof Date) {
    return dayjs(value).format('YYYY-MM-DD');
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (variant === 'usd' && value === undefined) {
    return '';
  }

  return value;
};

/**
 * This function enables compatibility with `react-hook-form` `register`.
 * `TextInput` expects the `ref` to be at `inputRef`.
 * It also removes the `min` and `max` properties from the `register` object, which are typed differently by `react-hook-form` and `TextInput`.
 * @param registers The `register` object from `react-hook-form`
 */
export const getRegister = (registers: UseFormRegisterReturn) => {
  return {
    ...registers,
    inputRef: registers.ref,
    max: undefined,
    min: undefined,
    ref: undefined,
  };
};
